// Presentation metadata for the IPEDS export wizard, transcribed from the design.
// The catalog is the full survey list the wizard will eventually cover; only the extracts
// listed in WIRED_EXTRACTS have a configured flow behind them in this release, so the rest
// render exactly as designed but cannot advance past Step 1.
import { WIZARD_TOKENS } from '../../../../../../components/StepWizard';

export const IPEDS_COLORS = {
    ...WIZARD_TOKENS,
    canvas: '#F5F7F9',
    heading: '#1F2A37',
    // Card body greys: the description sits below the title in weight and in tone.
    bodyMuted: '#6B7280',
    // Outline chips — white fill, coloured border and text, mixed case.
    feedBorder: '#7FC5DA',
    feedText: '#0A7FA0',
    warnBorder: '#E0A93B',
    warnText: '#B4700A',
    lockedBg: '#E8F6FA',
    bannerWarnBg: '#FFF8E7',
    bannerWarnBorder: '#F0C878',
    bannerWarnIcon: '#E8912A',
    bannerOkBg: '#EAF6EC',
    bannerOkBorder: '#A7D3AC',
    bannerOkIcon: '#3D9142',
    disabledText: '#AFB4BB',
    rowRule: '#EDF0F2',
    // Review Students summary cards and tabs.
    summaryLabel: '#6B7280',
    summaryOkText: '#3D9142',
    summaryErrorBg: '#FDEEE7',
    summaryErrorBorder: '#F0A27E',
    summaryErrorText: '#E8650A',
    tabSelectedBg: '#E6F5FA'
};

export const SFA_EXTRACT_CODE = 'SFA';
export const AWARDS_EXTRACT_CODE = 'AWARDS';
export const E12_EXTRACT_CODE = 'E12MONTH';
export const EF_EXTRACT_CODE = 'EF';

/**
 * Extracts that can leave Step 1. SFA runs end to end; Awards & Completions and 12-Month
 * Enrollment have their Configure Parameters step only, so they stop at Review Students
 * until that step is built. Fall Enrollment has Configure Parameters and Review Students; its
 * review stays empty until the extract is built.
 */
export const WIRED_EXTRACTS = [SFA_EXTRACT_CODE, AWARDS_EXTRACT_CODE, E12_EXTRACT_CODE, EF_EXTRACT_CODE];

/** Extracts whose Step 2 Next starts a backend report run. */
export const REPORT_EXTRACTS = [SFA_EXTRACT_CODE, AWARDS_EXTRACT_CODE];

export const generatesReport = (id) => REPORT_EXTRACTS.includes(id);

export const isExtractWired = (id) => WIRED_EXTRACTS.includes(id);

// `feeds` are the IPEDS surveys each extract supplies, shown as chips under the grain line.
// `statusKey` is a setup-warning chip; `disabled` greys the card the design shows as having
// no configuration at all. Ordered alphabetically by name, as in the design.
export const IPEDS_EXTRACTS = [
    {
        id: E12_EXTRACT_CODE,
        nameKey: 'ipedsWizard.extract12MonthName',
        descriptionKey: 'ipedsWizard.extract12MonthDescription',
        grainKey: 'ipedsWizard.extract12MonthGrain',
        feeds: ['E12']
    },
    {
        id: 'ADM',
        nameKey: 'ipedsWizard.extractAdmissionsName',
        descriptionKey: 'ipedsWizard.extractAdmissionsDescription',
        grainKey: 'ipedsWizard.extractAdmissionsGrain',
        feeds: ['ADM']
    },
    {
        id: AWARDS_EXTRACT_CODE,
        nameKey: 'ipedsWizard.extractAwardsName',
        descriptionKey: 'ipedsWizard.extractAwardsDescription',
        grainKey: 'ipedsWizard.extractAwardsGrain',
        feeds: ['C']
    },
    {
        id: 'COHORT',
        nameKey: 'ipedsWizard.extractCohortName',
        descriptionKey: 'ipedsWizard.extractCohortDescription',
        grainKey: 'ipedsWizard.extractCohortGrain',
        feeds: ['GR', 'GR200', 'OM'],
        statusKey: 'ipedsWizard.statusIncompleteSetup'
    },
    {
        id: 'CRSREG',
        nameKey: 'ipedsWizard.extractCourseRegName',
        descriptionKey: 'ipedsWizard.extractCourseRegDescription',
        grainKey: 'ipedsWizard.extractCourseRegGrain',
        feeds: ['EF', 'E12']
    },
    {
        id: EF_EXTRACT_CODE,
        nameKey: 'ipedsWizard.extractFallEnrollmentName',
        descriptionKey: 'ipedsWizard.extractFallEnrollmentDescription',
        grainKey: 'ipedsWizard.extractFallEnrollmentGrain',
        feeds: ['EF']
    },
    {
        id: 'NONCREDIT',
        nameKey: 'ipedsWizard.extractNoncreditName',
        descriptionKey: 'ipedsWizard.extractNoncreditDescription',
        grainKey: 'ipedsWizard.extractNoncreditGrain',
        feeds: [],
        statusKey: 'ipedsWizard.statusNotConfigured',
        disabled: true,
        disabledReasonKey: 'ipedsWizard.notConfiguredReason'
    },
    {
        id: 'DEMO',
        nameKey: 'ipedsWizard.extractDemographicsName',
        descriptionKey: 'ipedsWizard.extractDemographicsDescription',
        grainKey: 'ipedsWizard.extractDemographicsGrain',
        feeds: ['EF', 'E12', 'C', 'GR', 'OM']
    },
    {
        id: SFA_EXTRACT_CODE,
        nameKey: 'ipedsWizard.extractSfaName',
        descriptionKey: 'ipedsWizard.extractSfaDescription',
        grainKey: 'ipedsWizard.extractSfaGrain',
        feeds: ['SFA']
    },
    {
        id: 'TERMENR',
        nameKey: 'ipedsWizard.extractTermEnrollmentName',
        descriptionKey: 'ipedsWizard.extractTermEnrollmentDescription',
        grainKey: 'ipedsWizard.extractTermEnrollmentGrain',
        feeds: ['EF', 'GR', 'GR200', 'OM']
    }
];

export const getExtract = (id) => IPEDS_EXTRACTS.find((extract) => extract.id === id) || null;

// Report types come from the API select-items; these codes are the contract with the
// backend (IPEDSSFAReportService.ReportTypeDisplayNames). The Military and Veteran Benefits
// code is also enforced server-side by IPEDSSFASection2ReportRequestValidator, which rejects
// anything else outright - it must stay spelled exactly as IPEDSSFAConstants declares it.
export const REPORT_TYPE_ACADEMIC = 'AcademicReporter';
export const REPORT_TYPE_PROGRAM = 'ProgramReporter';
export const REPORT_TYPE_MILITARY_VETERAN = 'MilitaryAndVeteranBenefits';

// Terms scope the Academic Reporter population only; the other report types derive their
// own population, so the field is hidden (and not required) for them.
export const requiresTerms = (reportType) => reportType === REPORT_TYPE_ACADEMIC;

// PDF is the only output the IPEDS submission workflow accepts today. The other three stay
// in the list — the design shows all four — but render greyed out and cannot be picked.
export const OUTPUT_FORMATS = [
    { code: 'CSV', labelKey: 'ipedsWizard.formatCsv', extension: 'csv', disabled: true },
    { code: 'TAB', labelKey: 'ipedsWizard.formatTab', extension: 'txt', disabled: true },
    { code: 'XLSX', labelKey: 'ipedsWizard.formatExcel', extension: 'xlsx', disabled: true },
    { code: 'PDF', labelKey: 'ipedsWizard.formatPdf', extension: 'pdf' }
];

export const DEFAULT_OUTPUT_FORMAT = 'PDF';

// An unrecognised code falls back to the default rather than to the head of the list, which
// is a format the user cannot select.
export const getOutputFormat = (code) =>
    OUTPUT_FORMATS.find((format) => format.code === code)
    || OUTPUT_FORMATS.find((format) => format.code === DEFAULT_OUTPUT_FORMAT);

/**
 * IPEDS_{feed}_{reportingYear}.{ext} — the design's Step 4 filename, e.g.
 * IPEDS_SFA_2024-25.csv. The year keeps its hyphen; only characters that are illegal in a
 * filename are stripped, so an academic year written "2024/2025" still yields a valid name.
 */
export const buildExportFileName = (feedCode, reportingYearDisplay, outputFormatCode) => {
    const clean = (value) => String(value || '').replace(/[^a-zA-Z0-9-]/g, '');
    return `IPEDS_${clean(feedCode)}_${clean(reportingYearDisplay)}.${getOutputFormat(outputFormatCode).extension}`;
};
